import React, { FC, useCallback, useMemo, useState } from 'react';
import { Controller } from 'src/models/controller';
import { useSocket, useSocketGet } from 'src/utils/socket';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { getOrgName } from 'src/utils/helpers/referenceHelpers';
import { useIntl } from 'react-intl';
import { GenericMessages } from 'src/intl/GenericMessages';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import UpdateControllerModal from './UpdateControllerModal';
import ReassignControllerModal from './ReassignControllerModal';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthUserInfo, selectAuthUserOrg } from '../../redux/auth/selectors';
import { Roles } from '@dataquiver/stoneage-roles';
import { YesNoModal } from '../reusable-components/yes-no-modal/YesNoModal';
import { useListOrganizationsV2Query } from '../../redux/api/organizations';
import { useHistory } from 'react-router';
import { ControllerListPageActions } from '../../pages/controllers/ControllersListPage';
import { sentinelActions } from '../../redux/sentinel/actions';
import { Organization } from '../../models/organization';
import { ControllerSummaryDetails } from './ControllerSummaryDetails';
import { selectBleConnectedDevice } from 'src/redux/ble/selectors';
import { selectLogLocalDataByFilename } from 'src/redux/log/selectors';
import { UpdateFirmwareModal } from 'src/pages/mobile-controller-page/UpdateFirmwareModal';
import { BleService } from 'src/ble/BleService';
import { Firmware, SearchFirmwareRequest } from 'src/models/firmware';
import { useSocketSearch } from 'src/utils/socket/useSocketSearch';
import _ from 'lodash';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';

export interface ControllerSummaryCardProps {
  /**
   * The controller to display.
   */
  controller: Controller;
}

export const ControllerSummaryCard: FC<ControllerSummaryCardProps> = ({ controller }) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const [updateFirmwareModalOpen, setUpdateFirmwareModalOpen] = useState<boolean>(false);
  const [updateControllerDescriptionModalOpen, setUpdateControllerDescriptionModalOpen] = useState<boolean>(false);
  const [reassignModalOpen, setReassignModalOpen] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
  const [reassignmentOrgId, setReassignmentOrgId] = useState<string>(controller.org);
  const userOrg: Organization | undefined = useSelector(selectAuthUserOrg);
  const user = useSelector(selectAuthUserInfo);
  const connectedDevice = useSelector(selectBleConnectedDevice);
  const localLogFiles = useSelector(selectLogLocalDataByFilename);

  // Firmware
  const [fwSearchRequest] = useState<SearchFirmwareRequest>({
    hidden: 'false',
    page: 0,
    sortOrder: 'desc',
    pageSize: 10000,
    sortProperty: 'createdAt',
  });

  const { item: firmware } = useSocketGet(SocketContextSentinel, 'firmware', controller?.fw);
  const { items: firmwareSearchResult } = useSocketSearch(SocketContextSentinel, 'firmware', fwSearchRequest);

  const { data: organizations, isLoading } = useListOrganizationsV2Query({
    pageNumber: 0,
    pageSize: 1000,
    statuses: ['active'],
  });

  // Jobs
  const latestLiveJob = useMemo(() => {
    const localLogFilesList = Object.values(localLogFiles);
    const liveJobs = localLogFilesList.filter(log => log.pendingClose === false);

    if (liveJobs.length === 0) return;
    const latestLiveJobFilename = _.last(liveJobs)?.filename;

    return latestLiveJobFilename?.replace('.log', '');
  }, [localLogFiles]);

  // If non-stoneage user, remove controller from cached collection if the org has changed.
  const onControllerUpdated = React.useCallback(
    (controller: Controller) => {
      if (!Roles.isStoneAge(user?.role) && controller.org !== userOrg?._id) {
        dispatch(sentinelActions.removeOne({ key: 'controllers', id: controller._id }));
      }
    },
    [dispatch, user?.role, userOrg?._id],
  );
  const { update: updateController } = useSocket(SocketContextSentinel, 'controllers', undefined, onControllerUpdated);

  /**
   * Updates the firmware of the connected controller
   */
  const updateFirmwareVersion = useCallback(async (firmware: Firmware | undefined) => {
    if (BleService.controller?.updateFirmware && firmware) {
      try {
        await BleService.controller.updateFirmware(firmware);
        setUpdateFirmwareModalOpen(false);
      } catch (err) {
        console.error('Error updating firmware');
      }
    } else {
      console.error('Could not update firmware!', firmware);
    }
  }, []);

  const updateDescription = React.useCallback(
    async (description: string | undefined | null) => {
      setUpdateControllerDescriptionModalOpen(false);
      try {
        await updateController({
          _id: controller._id,
          update: {
            $set: { description: description },
          },
        });
      } catch (e) {
        console.error('Failed to perform update.');
      }
    },
    [controller._id, updateController],
  );

  const handleReassignment = React.useCallback(
    async (organizationId?: string) => {
      setReassignModalOpen(false);
      if (!organizationId) {
        return;
      }

      // If ctrl organization was set to the same organization as it started with, no need to update
      if (organizationId === controller.org) {
        return;
      }

      setIsConfirmationModalOpen(true);
      setReassignmentOrgId(organizationId);
    },
    [controller.org],
  );

  const handleConfirmReassignment = React.useCallback(
    async (confirmed: boolean) => {
      setIsConfirmationModalOpen(false);
      if (!confirmed) {
        return;
      }
      const organizationName = getOrgName(reassignmentOrgId, organizations ?? []);
      try {
        await updateController({
          _id: controller._id,
          update: {
            $set: { org: reassignmentOrgId },
          },
        });
        history.push('/controllers', {
          action: ControllerListPageActions.ReassignmentSuccessful,
          controllerPhysicalSerial: controller.physicalSerial,
          organizationName,
        });
      } catch (err) {
        console.error('Error thrown reassigning controller.');
        history.push('/controllers', {
          action: ControllerListPageActions.ReassignmentUnsuccessful,
          controllerPhysicalSerial: controller.physicalSerial,
          organizationName,
        });
      }
    },
    [controller._id, controller.physicalSerial, history, organizations, reassignmentOrgId, updateController],
  );

  const renderReassignButton = useMemo(() => {
    return (
      !isLoading && ((userOrg?.plan === 'FamilyShare' && Roles.isAdmin(user?.role)) || Roles.isStoneAge(user?.role))
    );
  }, [isLoading, user?.role, userOrg?.plan]);

  const reassignmentConfirmationDescription = useMemo(() => {
    const mainBody = formatMessage(ControllerMessages.confirmControllerReassignmentBody, {
      organizationName: getOrgName(reassignmentOrgId, organizations ?? []),
    });
    if (!Roles.isStoneAge(user?.role)) {
      return mainBody;
    }

    return `${mainBody} ${formatMessage(ControllerMessages.confirmControllerReassignmentBodyNonStoneAge)}`;
  }, [formatMessage, organizations, reassignmentOrgId, user?.role]);

  const controllerFirmwareVersion = useMemo(() => {
    const connectedDeviceVersion = connectedDevice?.firmwareVersion;
    const savedFirmwareVersion = firmware?.version;

    if (savedFirmwareVersion) {
      return savedFirmwareVersion;
    }

    if (connectedDeviceVersion) {
      return connectedDeviceVersion;
    }

    return formatMessage(FirmwareMessages.unknown);
  }, [connectedDevice?.firmwareVersion, firmware?.version, formatMessage]);

  const isControllerConnected = connectedDevice?.serialNumber === controller.ssn;

  return (
    <>
      <ControllerSummaryDetails
        controller={controller}
        onUpdateFirmware={isControllerConnected ? () => setUpdateFirmwareModalOpen(true) : undefined}
        firmwareVersion={controllerFirmwareVersion}
        onReassignController={renderReassignButton ? () => setReassignModalOpen(true) : undefined}
        onUpdateController={() => setUpdateControllerDescriptionModalOpen(true)}
        liveJob={latestLiveJob}
        organization={getOrgName(controller.org, organizations ?? [])}
        isControllerConnected={isControllerConnected}
      />
      <UpdateControllerModal
        onSave={(description: string | undefined | null) => updateDescription(description)}
        onClose={() => setUpdateControllerDescriptionModalOpen(false)}
        controllerDescription={controller.description}
        isOpen={updateControllerDescriptionModalOpen}
      />
      <ReassignControllerModal
        availableOrganizations={organizations ?? []}
        controllerOrganizationId={controller.org}
        isOpen={reassignModalOpen}
        onSave={handleReassignment}
      />
      <YesNoModal
        isOpen={isConfirmationModalOpen}
        title={formatMessage(ControllerMessages.reassignControllerHeader)}
        description={reassignmentConfirmationDescription}
        yesButtonText={formatMessage(ControllerMessages.reassignButtonLabel)}
        noButtonText={formatMessage(GenericMessages.cancelButtonLabel)}
        displayCloseButton={true}
        close={handleConfirmReassignment}
      />
      <UpdateFirmwareModal
        onSave={updateFirmwareVersion}
        isOpen={updateFirmwareModalOpen}
        firmwares={firmwareSearchResult?.results}
        onClose={() => setUpdateFirmwareModalOpen(false)}
      />
    </>
  );
};
